import React, { useContext, useState } from 'react';
import ReactPDF, {
  ClipPath,
  Defs,
  Document,
  Font,
  G,
  Image,
  Page,
  Path,
  pdf,
  StyleSheet,
  Svg,
  Text,
  View
} from '@react-pdf/renderer';
import soleilExtrabold from 'assets/fonts/Soleil-Extrabold.woff';
import soleilRegular from 'assets/fonts/Soleil-Regular.woff';
import { ReactComponent as PDFIcon } from 'assets/icons/pdf.svg';
import { Chart as ChartJS } from 'chart.js';
import dayjs, { Dayjs } from 'dayjs';

import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { gridExpandedSortedRowIdsSelector } from '@mui/x-data-grid-pro';
import { GridApiPro } from '@mui/x-data-grid-pro/models/gridApiPro';
import { NonEmptyDateRange } from '@mui/x-date-pickers-pro/internal/models';

import { getAggregatedStats } from '../apis/dashboard';
import { SessionContext } from '../auth';
import useAsyncEffect from '../hooks/useAsyncEffect';
import { HeaderContext } from '../layouts/main/header/context';
import { DashboardType, IAggregatedStats, ICommonCampaignRow } from '../types/global';
import { IProfile, IProfileDomain } from '../types/users';
import { makeFormatCurrency, makeFormatPercent, useFullYearFormat, useNoYearDateFormat } from '../utils/format';

import { formatValue } from './DateRangePicker';

interface PDFRow {
  id: string;
  campaign: string;
  products: string[];
  starts: string;
  ends: string;
  incrementalRevenue: string;
  conversionChange: string;
  cartCreationChange: string;
  grossRevenue: string;
  revenuePerVisit: string;
  revenuePerShopper: string;
}

interface PDFDownloadProps {
  dateRange: NonEmptyDateRange<Dayjs> | null;
  aggregatedStats: IAggregatedStats | null;
  chartRef: React.RefObject<ChartJS>;
  dashboardType: DashboardType;
  apiRef: React.MutableRefObject<GridApiPro>;
  selectedOffers: ICommonCampaignRow[] | null;
}

interface ReportProps {
  dateRange: NonEmptyDateRange<Dayjs> | null;
  aggregatedStats: IAggregatedStats;
  chartRef: React.RefObject<ChartJS>;
  rows: PDFRow[];
  customerLogo: string | null;
  domainURL: string;
  domainName: string;
  fullYearFormat: string;
  noYearFormat: string;
  profile: IProfile;
  chartImage: string;
  dashboardType: DashboardType;
  lifetimeStats: IAggregatedStats;
  orgStart: string;
}

Font.register({
  family: 'Soleil',
  fonts: [
    { src: soleilRegular },
    { src: soleilExtrabold, fontWeight: 800 },
  ],
});

const styles = StyleSheet.create({
  page: {
    fontFamily: 'Soleil',
    padding: '21pt 30pt 6pt 30pt',
  },
  topBar: {
    height: '39pt',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 20pt',
    backgroundColor: '#6A7AB8',
    marginBottom: '10pt'
  },
  metricalLogo: {
    height: '14pt',
    width: '76.8pt',
    marginRight: '15pt',
  },
  customerLogo: {
    height: '23pt',
  },
  header: {
    marginTop: '17pt',
    marginBottom: '18pt',
    fontSize: '24pt',
    lineHeight: 1.25,
    color: '#4559A7',
  },
  headerLogos: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  lifetimeStats: {
    flexDirection: 'row',
  },
  lifetimeStat: {
    paddingRight: '8pt',
  },
  lifetimeStatValue: {
    fontSize: '11pt',
    fontWeight: 800,
    color: 'white',
  },
  lifetimeStatLabel: {
    fontSize: '6pt',
    color: 'white',
    textTransform: 'uppercase',
    marginTop: 'auto',
  },
  metaRow: {
    flexDirection: 'row',
    color: '#4559A7',
    fontSize: '9pt',
    marginBottom: '4pt',
  },
  rowLabel: {
    textTransform: 'uppercase',
    width: '71pt',
  },
  aggregatedStats: {
    marginTop: '18pt',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  statValue: {
    fontSize: '33pt',
    fontWeight: 800,
    color: '#8E8E8E',
    alignSelf: 'flex-end',
  },
  statLabel: {
    backgroundColor: '#6A7AB8',
    fontSize: '10pt',
    fontWeight: 800,
    color: 'white',
    textTransform: 'uppercase',
    flexDirection: 'row',
    justifyContent: 'center',
    padding: '0 4pt',
  },
  chartImage: {
    margin: '5px 0',
    width: '100%',
  },
  bar: {
    height: '14pt',
    backgroundColor: '#6A7AB8',
    marginBottom: '5pt',
    color: 'white',
    fontSize: '10pt',
    fontWeight: 800,
    textTransform: 'uppercase',
    justifyContent: 'center',
    alignItems: 'center',
  },
  barContent: {
    flexDirection: 'row',
    alignItems: 'center',
    flexGrow: 1,
  },
  barWarningIcon: {
    transform: 'scale(0.75)',
    marginRight: '3pt',
  },
  tableRow: {
    borderBottom: '0.5pt solid #E6E8F0',
    height: '27pt',
    flexDirection: 'row',
    alignItems: 'center',
  },
  headerRow: {
    height: '50pt',
    paddingBottom: '10pt',
  },
  tableCell: {
    flexDirection: 'row',
    alignItems: 'center',
    width: '76pt',
    fontSize: '9pt',
    color: '#616161',
    paddingLeft: '12pt',
  },
  lastCell: {
    paddingRight: '12pt',
    width: '90pt',
  },
  headerCell: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'flex-end',
    fontSize: '8pt',
    color: '#4559A7',
    textTransform: 'uppercase',
    height: '100%',
  },
  headerCellContent: {
    backgroundColor: 'red',
    marginTop: 'auto',
    height: '8pt',
  },
  headerCellRight: {
    alignItems: 'flex-end',
    textAlign: 'right',
  },
  alignRight: {
    justifyContent: 'flex-end',
    textAlign: 'right',
  },
  product: {
    padding: '0 5pt',
    backgroundColor: '#8E8E8E',
    color: 'white',
    borderRadius: '10pt',
    marginRight: '3pt',
    textTransform: 'uppercase',
    fontSize: '7pt',
    fontWeight: 800,
  },
  dateWarning: {
    transform: 'scale(0.75)',
    marginLeft: '2pt',
  },
  column1: {
    width: '70pt',
  },
  column2: {
    width: '90pt',
  },
  column3: {
    width: '80pt',
  },
  column4: {
    width: '66pt',
  },
  table: {
    marginBottom: '8pt',
  },
  footer: {
    fontSize: '7pt',
    color: '#6A7AB8',
    textTransform: 'uppercase',
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 'auto',
    paddingTop: '18pt',
  },
});

function WarningIcon(props: ReactPDF.SVGProps) {
  return (
    <Svg width="17" height="16" viewBox="0 0 17 16" {...props}>
      <Path
        fill="white"
        d="M8.98151 1.56642C8.53763 0.811193 7.46546 0.811193 7.02158 1.56642L0.164626 13.2331C-0.292407 14.0107 0.256279 15 1.14459 15H14.8585C15.7468 15 16.2955 14.0107 15.8385 13.2331L8.98151 1.56642Z"
      />
      <Path
        fill="#FB8C00"
        d="M8.98151 1.56642C8.53763 0.811193 7.46546 0.811193 7.02158 1.56642L0.164626 13.2331C-0.292407 14.0107 0.256279 15 1.14459 15H14.8585C15.7468 15 16.2955 14.0107 15.8385 13.2331L8.98151 1.56642ZM8 5C8.53541 5 8.95377 5.46228 8.9005 5.99504L8.54975 9.50248C8.52151 9.78492 8.28384 10 8 10C7.71616 10 7.47849 9.78492 7.45025 9.50248L7.0995 5.99504C7.04623 5.46229 7.46459 5 8 5ZM8.00154 11C8.55383 11 9.00154 11.4477 9.00154 12C9.00154 12.5523 8.55383 13 8.00154 13C7.44926 13 7.00154 12.5523 7.00154 12C7.00154 11.4477 7.44926 11 8.00154 11Z"
      />
    </Svg>
  );
}

function MetricalLogo(props: ReactPDF.SVGProps) {
  return (
    <Svg width="479.967" height="87.474" viewBox="0 0 479.967 87.474" {...props}>
      <Defs>
          <ClipPath id="a">
              <Path d="M0 65.606h359.975V0H0Z"/>
          </ClipPath>
      </Defs>
      <G clip-path="url(#a)" transform="matrix(1.33333 0 0 -1.33333 0 87.474)">
        <Path d="M0 0v21.142c0 4.181-.789 8.126-5.128 8.126-4.418 0-6.311-3.945-6.311-9.309V0h-12.623v21.142c0 4.812-1.103 8.126-5.205 8.126-4.418 0-6.312-3.945-6.312-9.309V0h-12.622v38.892h12.622l-.078-5.601.157.078c1.973 3.235 5.759 6.233 10.729 6.233 5.365 0 9.625-1.815 11.596-7.1 2.762 4.733 6.627 7.1 12.386 7.1 9.939 0 13.49-6.785 13.49-16.172V0Z" fill="#fff" transform="translate(118.525 5.543)" />
        <Path d="M0 0c-5.206 0-7.889-4.103-8.046-7.81H7.968C7.889-4.26 5.286 0 0 0m19.564-14.989H-8.125c.552-5.601 4.812-8.125 9.939-8.125 5.049 0 9.467 2.209 11.676 3.708l4.97-6.548C17.198-26.9 12.07-32.266 0-32.266c-11.991 0-20.747 7.416-20.747 20.275C-20.747.868-11.28 8.204.237 8.204c11.518 0 19.486-7.652 19.486-19.012 0-1.893-.159-4.181-.159-4.181" fill="#fff" transform="translate(156.543 36.94)" />
        <Path d="M0 0v-13.963c0-3.392.236-5.759 4.103-5.759 1.971 0 3.786.71 3.786.71l.631-9.861c-.947-.236-4.417-1.263-8.994-1.263-10.097 0-12.227 6.154-12.227 15.069V0h-5.523v9.545h5.523v10.571H0V9.545h8.599V0Z" fill="#fff" transform="translate(197.089 34.811)" />
        <Path d="M0 0h-.553c-.71.395-2.366.947-4.575.947-6.232 0-7.81-5.838-7.81-10.966v-17.276h-12.623v38.892h12.623l-.079-5.523h.158c2.209 4.261 5.443 6.233 9.625 6.233 2.602 0 3.944-.71 3.944-.71z" fill="#fff" transform="translate(236.689 32.838)" />
        <Path d="M242.208 44.435h12.622V5.543h-12.622z" fill="#fff" />
        <Path d="M0 0c-10.65 0-20.511 7.258-20.511 20.195 0 13.017 10.019 20.275 20.668 20.275 9.941 0 14.437-4.102 14.437-4.102l-2.761-9.546a15.99 15.99 0 0 1-10.019 3.471c-5.364 0-9.624-3.707-9.624-9.86 0-6.154 3.866-10.019 9.782-10.019 5.917 0 9.861 3.786 9.861 3.786l2.998-9.545C14.437 4.339 10.334 0 0 0" fill="#fff" transform="translate(280.388 4.675)" />
        <Path d="M0 0c-3.866-.158-6.153-1.814-6.153-4.733 0-2.998 2.445-4.813 5.916-4.813 3.392 0 5.68 2.209 6.468 3.55V.395zm6.705-16.725-.236 4.103c-1.657-1.657-4.813-4.971-11.123-4.971-6.864 0-14.121 3.708-14.121 12.386 0 8.678 7.967 11.597 15.146 11.912l9.782.395v.868c0 4.101-2.919 6.231-7.967 6.231-4.97 0-9.941-2.209-11.913-3.47l-3.313 8.204c3.313 1.657 9.388 3.944 16.645 3.944 12.149 0 19.17-5.049 19.17-17.671v-21.931z" fill="#fff" transform="translate(316.895 22.268)" />
        <Path d="M341.031 62.027h12.622V5.736h-12.622z" fill="#fff" />
        <Path d="M0 0v-33.358h13.473V6.173Z" fill="#fff" transform="translate(5.98 38.902)" />
        <Path d="M0 0v-42.176h13.932V6.173Z" fill="#fff" transform="translate(24.677 47.72)" />
        <Path d="M0 0v-49.273h13.932V7.21Z" fill="#fff" transform="translate(43.834 54.817)" />
        <Path d="M0 0a7.153 7.153 0 0 0-7.154-7.153A7.154 7.154 0 1 0 0 0" fill="#fff" transform="translate(255.674 55.145)" />
      </G>
    </Svg>
  );
}

function Report(
  {
    customerLogo, domainURL, domainName, dateRange, fullYearFormat, noYearFormat,
    aggregatedStats, profile, chartImage, rows, dashboardType, lifetimeStats, orgStart,
  }: ReportProps,
) {
  const formatCurrency = makeFormatCurrency(profile);
  const formatCurrency2 = makeFormatCurrency(profile, 2);
  const formatPercent = makeFormatPercent(profile);
  const filteredRows = rows.filter((x) => !('offer' in x) && !('device' in x));
  const isPartialData = filteredRows.some((row) => (
    dateRange && (dayjs(row.starts).isBefore(dateRange[0]) || dayjs(row.ends).isAfter(dateRange[1]))
  ));
  const formattedDateRange = dateRange && formatValue(dateRange, fullYearFormat, noYearFormat);
  return (
    <Document>
      <Page size="LETTER" style={styles.page} wrap>
        <View style={styles.topBar} fixed>
          <View style={styles.headerLogos}>
            <MetricalLogo
              style={styles.metricalLogo}
            />
            {customerLogo && (
              <Image
                src={{
                  uri: customerLogo,
                  method: 'GET',
                  headers: {
                    'Cache-Control': 'no-cache',
                  },
                  body: '',
                }}
                style={styles.customerLogo}
              />
            )}
          </View>
          <View style={styles.lifetimeStats}>
            {'incrementalRevenue' in lifetimeStats ? (
              <>
                <View style={styles.lifetimeStat}>
                  <View style={styles.lifetimeStatValue}>
                    <Text>
                      {formatCurrency({ value: lifetimeStats.incrementalRevenue })}
                    </Text>
                  </View>
                  <View style={styles.lifetimeStatLabel}>
                    <Text>
                      Incremental revenue
                    </Text>
                  </View>
                </View>
                <View style={styles.lifetimeStat}>
                  <Text style={styles.lifetimeStatValue}>
                    {formatPercent({ value: lifetimeStats.cartCreationChange }, { sign: true })}
                  </Text>
                  <View style={styles.lifetimeStatLabel}>
                    <Text>
                      Carts created
                    </Text>
                  </View>
                </View>
                <View style={styles.lifetimeStat}>
                  <Text style={styles.lifetimeStatValue}>
                    {formatPercent({ value: lifetimeStats.conversionChange }, { sign: true })}
                  </Text>
                  <View style={styles.lifetimeStatLabel}>
                    <Text>
                      Carts saved
                    </Text>
                  </View>
                </View>
              </>
            ) : (
              <>
                <View style={styles.lifetimeStat}>
                  <Text style={styles.lifetimeStatValue}>
                    {formatCurrency({ value: lifetimeStats.grossRevenue })}
                  </Text>
                  <View style={styles.lifetimeStatLabel}>
                    <Text>
                      Gross revenue
                    </Text>
                  </View>
                </View>
                <View style={styles.lifetimeStat}>
                  <Text style={styles.lifetimeStatValue}>
                    {formatCurrency2({ value: lifetimeStats.revenuePerVisit })}
                  </Text>
                  <View style={styles.lifetimeStatLabel}>
                    <Text>
                      Revenue per visit
                    </Text>
                  </View>
                </View>
                <View style={styles.lifetimeStat}>
                  <Text style={styles.lifetimeStatValue}>
                    {formatCurrency2({ value: lifetimeStats.revenuePerShopper })}
                  </Text>
                  <View style={styles.lifetimeStatLabel}>
                    <Text>
                      Revenue per shopper
                    </Text>
                  </View>
                </View>
              </>
            )}
            <View>
              <View style={styles.lifetimeStatLabel}>
                <Text>
                  {`since ${dayjs(orgStart).format('MMM YYYY')}`}
                </Text>
              </View>
            </View>
          </View>
        </View>
        <Text style={styles.header}>
          Performance Report
        </Text>
        <View style={styles.metaRow}>
          <Text style={styles.rowLabel}>
            Domain:
          </Text>
          <Text>
            {domainName} ({domainURL})
          </Text>
        </View>
        {dateRange && (
          <View style={styles.metaRow}>
            <Text style={styles.rowLabel}>
              Timeframe:
            </Text>
            <Text>
              {formattedDateRange}
            </Text>
          </View>
        )}
        <View style={styles.aggregatedStats}>
          {'incrementalRevenue' in aggregatedStats ? (
            <>
              <View>
                <View style={styles.statValue}>
                  <Text>
                    {formatCurrency({ value: aggregatedStats.incrementalRevenue })}
                  </Text>
                </View>
                <View style={styles.statLabel}>
                  <Text>
                    Incremental revenue
                  </Text>
                </View>
              </View>
              <View>
                <Text style={styles.statValue}>
                  {formatPercent({ value: aggregatedStats.cartCreationChange }, { sign: true })}
                </Text>
                <View style={styles.statLabel}>
                  <Text>
                    Carts created
                  </Text>
                </View>
              </View>
              <View>
                <Text style={styles.statValue}>
                  {formatPercent({ value: aggregatedStats.conversionChange }, { sign: true })}
                </Text>
                <View style={styles.statLabel}>
                  <Text>
                    Carts saved
                  </Text>
                </View>
              </View>
            </>
          ) : (
            <>
              <View>
                <Text style={styles.statValue}>
                  {formatCurrency({ value: aggregatedStats.grossRevenue })}
                </Text>
                <View style={styles.statLabel}>
                  <Text>
                    Gross revenue
                  </Text>
                </View>
              </View>
              <View>
                <Text style={styles.statValue}>
                  {formatCurrency2({ value: aggregatedStats.revenuePerVisit })}
                </Text>
                <View style={styles.statLabel}>
                  <Text>
                    Revenue per visit
                  </Text>
                </View>
              </View>
              <View>
                <Text style={styles.statValue}>
                  {formatCurrency2({ value: aggregatedStats.revenuePerShopper })}
                </Text>
                <View style={styles.statLabel}>
                  <Text>
                    Revenue per shopper
                  </Text>
                </View>
              </View>
            </>
          )}
        </View>
        <Image
          src={chartImage}
          style={styles.chartImage}
        />
        <View style={styles.bar} />
        <View style={styles.table}>
          <View style={[styles.tableRow, styles.headerRow]}>
            <View style={[styles.tableCell, styles.headerCell, styles.column2]}>
              <Text>
                Campaign
              </Text>
            </View>
            <View style={[styles.tableCell, styles.headerCell, styles.column4]}>
              <Text>
                Product
              </Text>
            </View>
            <View style={[styles.tableCell, styles.headerCell, styles.column3]}>
              <Text>
                Starts
              </Text>
            </View>
            <View style={[styles.tableCell, styles.headerCell, styles.column3]}>
              <Text>
                Ends
              </Text>
            </View>
            {dashboardType === DashboardType.Incremental ? (
              <>
                <View style={[styles.tableCell, styles.headerCell, styles.column1, styles.headerCellRight]}>
                  <Text>
                    Incremental
                  </Text>
                  <Text>
                    revenue
                  </Text>
                </View>
                <View style={[styles.tableCell, styles.headerCell, styles.column1, styles.headerCellRight]}>
                  <Text>
                    Purchase rate
                  </Text>
                  <Text>
                    % change
                  </Text>
                </View>
                <View style={[styles.tableCell, styles.headerCell, styles.headerCellRight, styles.lastCell]}>
                  <Text>
                    Cart creation
                  </Text>
                  <Text>
                    % change
                  </Text>
                </View>
              </>
            ) : (
              <>
                <View style={[styles.tableCell, styles.headerCell, styles.column1, styles.headerCellRight]}>
                  <Text>
                    Gross
                  </Text>
                  <Text>
                    revenue
                  </Text>
                </View>
                <View style={[styles.tableCell, styles.headerCell, styles.column1, styles.headerCellRight]}>
                  <Text>
                    Revenue
                  </Text>
                  <Text>
                    per visit
                  </Text>
                </View>
                <View style={[styles.tableCell, styles.headerCell, styles.headerCellRight, styles.lastCell]}>
                  <Text>
                    Revenue
                  </Text>
                  <Text>
                    per shopper
                  </Text>
                </View>
              </>
            )}
          </View>
          {filteredRows.map((row) => (
            <View key={row.id} style={styles.tableRow} wrap={false}>
              <Text style={[styles.tableCell, styles.column2]}>
                {row.campaign}
              </Text>
              <View style={[styles.tableCell, styles.column4]}>
                {row.products.sort().map((product) => (
                  <View key={product} style={styles.product}>
                    <Text>
                      {product}
                    </Text>
                  </View>
                ))}
              </View>
              <View style={[styles.tableCell, styles.column3]}>
                <Text>
                  {row.starts}
                </Text>
                {dateRange && dayjs(row.starts).isBefore(dateRange[0]) && (
                  <WarningIcon style={styles.dateWarning} />
                )}
              </View>
              <View style={[styles.tableCell, styles.column3]}>
                <Text>
                  {row.ends}
                </Text>
                {dateRange && dayjs(row.ends).isAfter(dateRange[1]) && (
                  <WarningIcon style={styles.dateWarning} />
                )}
              </View>
              {dashboardType === DashboardType.Incremental ? (
                <>
                  <Text style={[styles.tableCell, styles.column1, styles.alignRight]}>
                    <Text>
                      {row.incrementalRevenue}
                    </Text>
                  </Text>
                  <Text style={[styles.tableCell, styles.column1, styles.alignRight]}>
                    <Text>
                      {row.conversionChange}
                    </Text>
                  </Text>
                  <Text style={[styles.tableCell, styles.alignRight, styles.lastCell]}>
                    <Text>
                      {row.cartCreationChange}
                    </Text>
                  </Text>
                </>
              ) : (
                <>
                  <Text style={[styles.tableCell, styles.column1, styles.alignRight]}>
                    <Text>
                      {row.grossRevenue}
                    </Text>
                  </Text>
                  <Text style={[styles.tableCell, styles.column1, styles.alignRight]}>
                    <Text>
                      {row.revenuePerVisit}
                    </Text>
                  </Text>
                  <Text style={[styles.tableCell, styles.alignRight, styles.lastCell]}>
                    <Text>
                      {row.revenuePerShopper}
                    </Text>
                  </Text>
                </>
              )}
            </View>
          ))}
        </View>
        <View style={styles.bar} wrap={false}>
          {isPartialData && (
            <View style={styles.barContent}>
              <WarningIcon style={styles.barWarningIcon} />
              <Text>
                Table shows data for the period {formattedDateRange}.
              </Text>
            </View>
          )}
        </View>
        <View fixed style={styles.footer}>
          <Text
            render={({ pageNumber, totalPages }) => (
              `Page ${pageNumber} of ${totalPages}`
            )}
          />
          <Text>
            Copyright (c) 2023 Metrical Inc. All rights reserved
          </Text>
        </View>
      </Page>
    </Document>
  );
}

export default function PDFDownload(props: PDFDownloadProps) {
  const {
    aggregatedStats,
    dateRange,
    apiRef,
    selectedOffers,
    ...rest
  } = props;
  const { profile } = useContext(SessionContext) as { profile: IProfile };
  const { domainId, dashboardType, organizationId } = useContext(HeaderContext);
  const { logoUrl, url, name } = profile.organizations
    .flatMap((x) => x.domains)
    .find((x) => x.id === domainId) as IProfileDomain;
  const fullYearFormat = useFullYearFormat();
  const noYearFormat = useNoYearDateFormat();
  const [lifetimeStats, setLifetimeStats] = useState<IAggregatedStats | null>(null);
  useAsyncEffect(async () => {
    const { data } = await getAggregatedStats(domainId, dashboardType);
    setLifetimeStats(data);
  }, [domainId, dashboardType]);
  const { start: orgStart } = profile.organizations
    .find((x) => x.id === organizationId)!
    .domains.find((x) => x.id === domainId) as IProfileDomain;
  const { chartRef } = props;
  const disabled = !aggregatedStats || !lifetimeStats;
  const button = (
    <IconButton
      sx={{
        margin: '-5px -9px -5px 4px',
        display: { xs: 'none', lg: 'inline-flex' },
      }}
      onClick={() => {
        if (!aggregatedStats || !lifetimeStats || !dateRange) {
          return;
        }
        const chart = chartRef.current as ChartJS;
        const [initialWidth, initialHeight] = [chart.width, chart.height];
        chart.options.devicePixelRatio = 4;
        chart.resize(1000, 350);
        setTimeout(async () => {
          const image = chart.toBase64Image();
          chart.options.devicePixelRatio = window.devicePixelRatio;
          chart.resize(initialWidth, initialHeight);
          const filteredSortedRowIds = gridExpandedSortedRowIdsSelector(apiRef);
          const rows = filteredSortedRowIds.map((id): PDFRow => {
            const columns = [
              'campaign', 'starts', 'ends', 'incrementalRevenue', 'conversionChange',
              'cartCreationChange', 'grossRevenue', 'revenuePerVisit', 'revenuePerShopper',
            ];
            return {
              id: `${id}`,
              products: apiRef.current.getCellParams(id, 'products').value,
              ...Object.fromEntries(columns.map((column) => (
                [column, apiRef.current.getCellParams(id, column).formattedValue]
              ))),
            } as PDFRow;
          }).filter((row) => (
            !selectedOffers || selectedOffers.find((x) => x.campaign === row.campaign)
          ));
          const blob = await pdf(
            <Report
              customerLogo={logoUrl}
              domainURL={url}
              domainName={name}
              fullYearFormat={fullYearFormat}
              noYearFormat={noYearFormat}
              profile={profile}
              chartImage={image}
              rows={rows}
              aggregatedStats={aggregatedStats}
              lifetimeStats={lifetimeStats}
              orgStart={orgStart}
              dateRange={dateRange}
              {...rest}
            />
          ).toBlob();
          const link = document.createElement('a');
          link.href = URL.createObjectURL(blob);
          const now = dayjs();
          const formattedRange = `${dateRange[0].format('YYYYMMDD')}-${dateRange[1].format('YYYYMMDD')}`;
          link.download = `${name} - ${formattedRange} - ${now.format('YYYYMMDD_HHmm')}.pdf`;
          link.click();
        }, 1);
      }}
      disabled={disabled}
    >
      <PDFIcon />
    </IconButton>
  );
  return disabled ? button : (
    <Tooltip title="Download PDF report">
      {button}
    </Tooltip>
  );
}
